import {
    Asset,
    BoundingBox,
    Color,
    Entity,
    Mat4,
    MeshInstance,
    Quat,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';
import { Serializer } from './serializer';
import { Transform } from './transform';

const vec = new Vec3();
const invWorld = new Mat4();

// A traditional polygonal model (glB/glTF) imported alongside gaussian splats.
// Mirrors the parts of the Splat element interface that the selection, scene
// list and transform gizmo rely on (entity, move, getPivot, name, visible,
// worldBound) so meshes behave as first-class scene elements.
class Model extends Element {
    asset: Asset;
    entity: Entity;

    // original glB/glTF bytes, retained so the model can be re-saved into .ssproj
    fileData: Uint8Array = null;

    // Real-world unit declared by the source file, when it had one (CAD formats do;
    // most mesh formats do not). Nothing consumes this yet - it is captured at import
    // because it cannot be recovered afterwards, and measurement calibration needs it.
    unit: string | null = null;

    _name = '';
    _visible = true;

    localBoundStorage = new BoundingBox();
    worldBoundStorage = new BoundingBox();

    constructor(asset: Asset, filename: string) {
        super(ElementType.model);

        this.asset = asset;
        this._name = filename;

        // instantiate a render entity from the loaded container resource
        const resource = asset.resource as any;
        this.entity = resource.instantiateRenderEntity();
    }

    destroy() {
        super.destroy();
        this.entity.destroy();
        this.asset.registry.remove(this.asset);
        this.asset.unload();
    }

    get meshInstances(): MeshInstance[] {
        const result: MeshInstance[] = [];
        this.entity.findComponents('render').forEach((r: any) => {
            result.push(...r.meshInstances);
        });
        return result;
    }

    async add() {
        this.scene.attachEntity(this);
        this.updateWorldBound();
    }

    remove() {
        this.scene.detachEntity(this);
        this.scene.boundDirty = true;
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.entity.getWorldTransform().data);
        serializer.pack(this.visible);
    }

    onPreRender() {
        const events = this.scene.events;
        const selected = this.scene.camera.renderOverlays && events.invoke('selection') === this;

        // ancestor groups can hide this element without changing its own `visible` flag,
        // so the per-frame write goes through the same computation as the setter rather
        // than through `this.visible`, which would undo subtree hiding one frame later
        this.updateEffectiveVisibility();

        // draw a wireframe bound when this model is the active selection
        if (this.entity.enabled && selected && events.invoke('camera.bound')) {
            const bound = this.worldBound;
            this.scene.app.drawWireAlignedBox(
                bound.getMin(),
                bound.getMax(),
                Color.WHITE,
                true,
                this.scene.gizmoLayer
            );
        }
    }

    focalPoint() {
        return this.worldBound.center;
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const { entity } = this;
        if (position) {
            entity.setLocalPosition(position);
        }
        if (rotation) {
            entity.setLocalRotation(rotation);
        }
        if (scale) {
            entity.setLocalScale(scale);
        }

        this.updateWorldBound();
        this.scene.events.fire('model.moved', this);
    }

    // recompute world (and derived local) bound from the mesh instances
    private updateWorldBound() {
        const instances = this.meshInstances;
        if (instances.length === 0) {
            this.scene.boundDirty = true;
            return;
        }

        this.worldBoundStorage.copy(instances[0].aabb);
        for (let i = 1; i < instances.length; ++i) {
            this.worldBoundStorage.add(instances[i].aabb);
        }

        // derive a local-space bound for pivot placement
        invWorld.copy(this.entity.getWorldTransform()).invert();
        this.localBoundStorage.setFromTransformedAabb(this.worldBoundStorage, invWorld);

        this.scene.boundDirty = true;
    }

    get localBound() {
        return this.localBoundStorage;
    }

    get worldBound() {
        return this.worldBoundStorage;
    }

    get worldTransform() {
        return this.entity.getWorldTransform();
    }

    set name(value: string) {
        if (value !== this._name) {
            this._name = value;
            this.scene?.events.fire('model.name', this);
        }
    }

    get name() {
        return this._name;
    }

    set visible(value: boolean) {
        if (value !== this._visible) {
            this._visible = value;
            this.updateEffectiveVisibility();
            this.scene?.events.fire('model.visibility', this);
        }
    }

    get visible() {
        return this._visible;
    }

    // Rendering depends on this element AND every ancestor group being visible, so hiding
    // a group hides its contents without destroying each child's own visibility state.
    // Guarded for the window before scene.add(), when there is no scene to ask.
    updateEffectiveVisibility() {
        this.entity.enabled = this.scene ? this.scene.effectiveVisibility(this) : this._visible;
    }

    // pivot placement for the transform gizmo (matches Splat.getPivot)
    getPivot(mode: 'center' | 'boundCenter', selection: boolean, result: Transform) {
        const { entity } = this;
        switch (mode) {
            case 'center':
                result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
                break;
            case 'boundCenter': {
                entity.getLocalTransform().transformPoint(this.localBound.center, vec);
                result.set(vec, entity.getLocalRotation(), entity.getLocalScale());
                break;
            }
        }
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const pack4 = (q: Quat) => [q.x, q.y, q.z, q.w];
        return {
            name: this.name,
            position: pack3(this.entity.getLocalPosition()),
            rotation: pack4(this.entity.getLocalRotation()),
            scale: pack3(this.entity.getLocalScale()),
            visible: this.visible
        };
    }

    docDeserialize(doc: any) {
        const { name, position, rotation, scale, visible } = doc;
        this.name = name;
        this.move(new Vec3(position), new Quat(rotation), new Vec3(scale));
        this.visible = visible;
    }
}

export { Model };
